import { useState } from "react"
import type { ReactNode } from "react"
import { faChevronDown } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { ButtonBase, Collapse, IconButton, Stack, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

const useStyles = makeStyles<void, "toggle">()((theme, _params, classes) => ({
    section: {
        padding: ".875rem 1.125rem",
        borderRadius: ".875rem",
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        backgroundColor: theme.palette.background.paper,
        transition: theme.transitions.create("padding-bottom", { duration: theme.transitions.duration.standard }),
    },
    sectionOpen: {
        paddingBottom: ".5rem",
    },
    header: {
        width: "100%",
        textAlign: "left",
        [`&:hover .${classes.toggle}`]: {
            backgroundColor: theme.palette.action.hover,
        },
    },
    headerText: {
        flex: 1,
        minWidth: 0,
    },
    summary: {
        paddingTop: ".25rem",
    },
    summaryText: {
        color: theme.palette.text.secondary,
    },
    toggle: {},
    content: {
        paddingTop: ".5rem",
    },
    chevron: {
        fontSize: "1rem",
        color: theme.palette.tailwind.slate[400],
        transition: theme.transitions.create("transform", { duration: theme.transitions.duration.shorter }),
    },
    chevronOpen: {
        transform: "rotate(180deg)",
    },
}))

type Props = Readonly<{
    title: ReactNode
    headerAside?: ReactNode
    summary?: ReactNode
    collapsedSummary?: ReactNode
    isDefaultOpen?: boolean
    children: ReactNode
}>

export function MeetingRoomDrawerSection({
    title,
    headerAside,
    summary,
    collapsedSummary,
    isDefaultOpen = true,
    children,
}: Props) {
    const { classes, cx } = useStyles()
    const [isOpen, setIsOpen] = useState(isDefaultOpen)

    return (
        <Stack className={cx(classes.section, isOpen && classes.sectionOpen)}>
            <Stack
                component={ButtonBase}
                aria-expanded={isOpen}
                disableRipple
                direction="row"
                alignItems="flex-start"
                spacing=".75rem"
                className={classes.header}
                onClick={() => setIsOpen((open) => !open)}
            >
                <Stack className={classes.headerText}>
                    <Stack direction="row" alignItems="center" spacing=".5rem">
                        <Typography variant="subtitle1" noWrap>
                            {title}
                        </Typography>
                        {headerAside}
                    </Stack>
                    {summary && (
                        <Typography variant="body2" noWrap className={cx(classes.summary, classes.summaryText)}>
                            {summary}
                        </Typography>
                    )}
                    {collapsedSummary && (
                        <Collapse in={!isOpen}>
                            <Typography
                                variant="body2"
                                component="div"
                                noWrap
                                className={cx(classes.summary, classes.summaryText)}
                            >
                                {collapsedSummary}
                            </Typography>
                        </Collapse>
                    )}
                </Stack>
                <IconButton component="span" size="small" tabIndex={-1} className={classes.toggle}>
                    <FontAwesomeIcon
                        icon={faChevronDown}
                        className={cx(classes.chevron, isOpen && classes.chevronOpen)}
                    />
                </IconButton>
            </Stack>
            <Collapse in={isOpen}>
                <Stack className={classes.content}>{children}</Stack>
            </Collapse>
        </Stack>
    )
}
